import { useEffect, useState } from 'react';
import {
  Sliders, ShieldAlert, AlertCircle, AlertTriangle, Save, Info, Building2,
  CheckCircle2, Lock,
} from 'lucide-react';

import PageHeader from '../../../components/ui/PageHeader';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';

import { listMatchModes, updateMatchMode, listRouting } from '../../../api/mec';
import { useAuth } from '../../../contexts/AuthContext';
import { formatRelative } from '../../../utils/formatters';

// MEC Match Mode settings (per MEC_REQUIREMENTS.md §6 + §7).
//
// D-012: Mode A is the default for first 90 days.
// Backend endpoints (GET/PUT /api/mec/match-mode) may not be deployed yet;
// when 404 we hydrate from listRouting() (default mode A, threshold 0.95).

const MODES = [
  {
    key: 'A',
    label: 'Mode A — Hold for Review',
    tone: 'recovery',
    summary: 'Conservative (default).',
    description:
      'Tier 2 matches sit in the pending_review queue. They do NOT count toward Matched / WIP / Variance until a user confirms.',
  },
  {
    key: 'B',
    label: 'Mode B — Auto-bucket',
    tone: 'action',
    summary: 'Faster close.',
    description:
      'Tier 2 matches auto-flow into A / WIP buckets. Each is logged for retroactive review and sampling.',
  },
  {
    key: 'C',
    label: 'Mode C — Hybrid by score',
    tone: 'insight',
    summary: 'Best of both, once calibrated.',
    description:
      'Score ≥ threshold → auto-bucket. Score < threshold → hold for review. Threshold tunable per shop (default 0.95).',
  },
];

const ALLOWED_ROLES = ['super_admin', 'bookkeeper'];

export default function MatchModeSettings() {
  const { user } = useAuth();
  const canEdit = ALLOWED_ROLES.includes(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  // Load + hydrate
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setEndpointMissing(false);

    listMatchModes()
      .then((data) => {
        if (!alive) return;
        const shopRows = (data?.shops || data || []).map(hydrateRow);
        setRows(shopRows.filter((r) => r.shop_id !== 7515));
      })
      .catch((err) => {
        if (!alive) return;
        if (/404|not found/i.test(err?.message || '')) {
          // Fallback: hydrate from routing with defaults.
          setEndpointMissing(true);
          listRouting()
            .then((data) => {
              if (!alive) return;
              const shops = (data?.shops || []).filter((s) => s.shop_id !== 7515);
              setRows(shops.map((s) => hydrateRow({
                shop_id: s.shop_id,
                shop_name: s.shop_name,
                mode: 'A',
                threshold: 0.95,
                updated_at: null,
              })));
            })
            .catch((err2) => {
              if (!alive) return;
              setError(err2?.message || 'Failed to load shops');
            });
        } else {
          setError(err?.message || 'Failed to load match modes');
        }
      })
      .finally(() => alive && setLoading(false));

    return () => { alive = false; };
  }, []);

  const updateRow = (shopId, patch) => {
    setRows((prev) => prev.map((r) => r.shop_id === shopId ? { ...r, ...patch } : r));
  };

  const onModeChange = (shopId, mode) => {
    updateRow(shopId, { mode, _dirty: true, _saveError: null, _savedAt: null });
  };

  const onThresholdChange = (shopId, raw) => {
    let n = parseFloat(raw);
    if (isNaN(n)) n = 0.95;
    n = Math.min(1, Math.max(0, n));
    updateRow(shopId, { threshold: n, _dirty: true, _saveError: null, _savedAt: null });
  };

  const saveRow = async (row) => {
    if (!canEdit) return;
    updateRow(row.shop_id, { _saving: true, _saveError: null });
    try {
      const updated = await updateMatchMode(row.shop_id, row.mode, row.threshold);
      updateRow(row.shop_id, {
        ...hydrateRow({ ...row, ...updated }),
        _dirty: false,
        _saving: false,
        _saveError: null,
        _savedAt: new Date().toISOString(),
      });
    } catch (err) {
      if (/404|not found/i.test(err?.message || '')) {
        updateRow(row.shop_id, {
          _saving: false,
          _saveError: 'Backend endpoint not yet deployed. Setting will persist once /api/mec/match-mode is live.',
        });
      } else {
        updateRow(row.shop_id, {
          _saving: false,
          _saveError: err?.message || 'Save failed',
        });
      }
    }
  };

  return (
    <div>
      <PageHeader
        title="Match Mode"
        description="Per-shop Tier 2 auto-bucket policy. Conservative default = Mode A; tune to Mode B or C as confidence builds."
        breadcrumbs={[
          { label: 'Month-End Close', href: '/mec' },
          { label: 'Settings' },
          { label: 'Match Mode' },
        ]}
      />

      {/* Role gate banner */}
      {!canEdit && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-[var(--radius-md)] bg-[var(--color-action-50)] border border-[var(--color-action-100)]">
          <Lock size={16} className="shrink-0 mt-0.5 text-[var(--color-action-600)]" />
          <div className="text-[13px] text-[var(--color-action-700)] leading-relaxed">
            <strong className="font-semibold">View-only access.</strong>{' '}
            Only <code className="font-mono text-[12px]">super_admin</code> or <code className="font-mono text-[12px]">bookkeeper</code> roles can change Match Mode. You can review settings below but Save will be disabled.
          </div>
        </div>
      )}

      {endpointMissing && canEdit && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-[var(--radius-md)] bg-[var(--color-action-50)] border border-[var(--color-action-100)]">
          <AlertTriangle size={16} className="shrink-0 mt-0.5 text-[var(--color-action-600)]" />
          <div className="text-[13px] text-[var(--color-action-700)] leading-relaxed">
            <strong className="font-semibold">Endpoint not yet deployed.</strong>{' '}
            Showing default values (Mode A, threshold 0.95). Mode toggles will persist once the Backend agent ships <code className="font-mono text-[12px] bg-white/60 px-1 py-0.5 rounded">/api/mec/match-mode</code>.
          </div>
        </div>
      )}

      {/* Explanation card */}
      <Card className="mb-5">
        <div className="flex items-start gap-3 mb-4">
          <Sliders size={18} className="text-[var(--color-warm-500)] shrink-0 mt-0.5" />
          <div>
            <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
              How Tier 2 matching works
            </div>
            <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
              When a QBO line memo lacks a PO# but a Tekmetric PO matches by vendor + amount + date, the matcher scores it 0.0–1.0. Each shop picks how aggressive to be:
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {MODES.map((m) => (
            <div
              key={m.key}
              className={`
                rounded-[var(--radius-md)] border bg-[var(--color-cream-50)]
                p-3
                border-[var(--color-warm-100)]
              `}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Badge tone={m.tone} size="xs">{m.key}</Badge>
                <span className="text-[13px] font-semibold text-[var(--color-warm-900)]">
                  {m.summary}
                </span>
              </div>
              <div className="text-[12px] text-[var(--color-warm-700)] mb-1.5">
                {m.label}
              </div>
              <div className="text-[11px] text-[var(--color-warm-500)] leading-snug">
                {m.description}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2 text-[11px] text-[var(--color-warm-500)]">
          <Info size={11} className="shrink-0 mt-0.5" />
          <span>
            <strong>D-012 default:</strong> Mode A for first 90 days to build calibration data. Re-evaluate per-shop after acceptance-rate metrics on the Dashboard look stable.
          </span>
        </div>
      </Card>

      {error && (
        <Card className="mb-5">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <div>
              <div className="text-[14px] font-semibold">Couldn't load match modes.</div>
              <div className="text-[12px] text-[var(--color-warm-600)] mt-1">{error}</div>
            </div>
          </div>
        </Card>
      )}

      {loading && (
        <Card>
          <div className="flex items-center gap-3 py-8 justify-center text-[var(--color-warm-500)]">
            <span className="inline-block w-4 h-4 border-2 border-[var(--color-warm-300)] border-t-[var(--color-warm-700)] rounded-full animate-spin" />
            <span className="text-[13px]">Loading per-shop match modes…</span>
          </div>
        </Card>
      )}

      {!loading && !error && rows.length === 0 && (
        <Card>
          <div className="text-center py-10">
            <Building2 size={28} className="mx-auto mb-3 text-[var(--color-warm-400)]" />
            <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
              No shops on file
            </div>
            <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
              The Month-End Close database doesn't have any shops seeded.
            </div>
          </div>
        </Card>
      )}

      {!loading && !error && rows.length > 0 && (
        <Card padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-[var(--color-warm-100)] bg-[var(--color-cream-100)]">
                  <Th>Shop</Th>
                  <Th>Mode</Th>
                  <Th>Threshold (Mode C only)</Th>
                  <Th align="right">Save</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <ModeRow
                    key={row.shop_id}
                    row={row}
                    canEdit={canEdit}
                    onModeChange={onModeChange}
                    onThresholdChange={onThresholdChange}
                    onSave={() => saveRow(row)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function hydrateRow(raw) {
  return {
    shop_id: raw.shop_id,
    shop_name: raw.shop_name,
    mode: raw.mode || 'A',
    threshold: raw.threshold != null ? Number(raw.threshold) : 0.95,
    updated_at: raw.updated_at || null,
    _dirty: false,
    _saving: false,
    _saveError: null,
    _savedAt: null,
  };
}

function Th({ children, align = 'left' }) {
  return (
    <th
      className={`
        px-4 py-2.5 text-[10px] font-bold uppercase tracking-[1.2px]
        text-[var(--color-warm-500)]
        ${align === 'right' ? 'text-right' : 'text-left'}
      `}
    >
      {children}
    </th>
  );
}

function ModeRow({ row, canEdit, onModeChange, onThresholdChange, onSave }) {
  return (
    <tr className="border-b border-[var(--color-warm-100)] last:border-b-0 hover:bg-[var(--color-cream-100)]/40 transition-colors">
      {/* Shop */}
      <td className="px-4 py-3 align-middle">
        <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
          {row.shop_name || `Shop ${row.shop_id}`}
        </div>
        <div className="text-[11px] text-[var(--color-warm-500)] mono-nums mt-0.5">
          ID {row.shop_id}
        </div>
      </td>

      {/* Mode radio */}
      <td className="px-4 py-3 align-middle">
        <div className="inline-flex items-center gap-1 bg-[var(--color-cream-100)] rounded-[var(--radius-full)] p-1 border border-[var(--color-warm-100)]">
          {MODES.map((m) => {
            const active = row.mode === m.key;
            return (
              <label
                key={m.key}
                className={`
                  inline-flex items-center gap-1.5 px-2.5 h-7 text-[12px] font-semibold
                  rounded-[var(--radius-full)] cursor-pointer transition-colors
                  ${active
                    ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)] shadow-[var(--shadow-xs)]'
                    : 'text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)]'}
                  ${!canEdit ? 'cursor-not-allowed opacity-70' : ''}
                `}
                title={m.label}
              >
                <input
                  type="radio"
                  name={`mode-${row.shop_id}`}
                  value={m.key}
                  checked={active}
                  disabled={!canEdit || row._saving}
                  onChange={() => onModeChange(row.shop_id, m.key)}
                  className="sr-only"
                />
                {m.key}
              </label>
            );
          })}
        </div>
      </td>

      {/* Threshold slider */}
      <td className="px-4 py-3 align-middle min-w-[240px]">
        {row.mode === 'C' ? (
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={row.threshold}
              disabled={!canEdit || row._saving}
              onChange={(e) => onThresholdChange(row.shop_id, e.target.value)}
              className="flex-1 accent-[var(--color-warm-900)] disabled:opacity-40"
            />
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={row.threshold.toFixed(2)}
              disabled={!canEdit || row._saving}
              onChange={(e) => onThresholdChange(row.shop_id, e.target.value)}
              className="w-16 h-8 px-2 text-[12px] tabular-nums bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-sm)] focus:outline-none focus:border-[var(--color-warm-400)] disabled:opacity-50"
            />
          </div>
        ) : (
          <span className="text-[11px] text-[var(--color-warm-500)] italic">
            n/a — Mode {row.mode} doesn't use a threshold
          </span>
        )}
      </td>

      {/* Save */}
      <td className="px-4 py-3 align-middle text-right">
        <div className="flex flex-col items-end gap-1">
          <Button
            size="sm"
            variant={row._dirty ? 'primary' : 'ghost'}
            disabled={!canEdit || !row._dirty || row._saving}
            loading={row._saving}
            leftIcon={!row._saving && <Save size={13} />}
            onClick={onSave}
          >
            {row._dirty ? 'Save' : 'Saved'}
          </Button>
          {row._saveError && (
            <span className="text-[11px] text-[var(--color-danger-600)] max-w-[220px] text-right">
              {row._saveError}
            </span>
          )}
          {!row._saveError && (row._savedAt || row.updated_at) && (
            <span className="inline-flex items-center gap-1 text-[11px] text-[var(--color-warm-500)]">
              {row._savedAt && (
                <CheckCircle2 size={11} className="text-[var(--color-recovery-500)]" />
              )}
              Last saved {formatRelative(row._savedAt || row.updated_at)}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}
